import './AddVenueModal.css';

import React, { ChangeEvent, FormEvent } from 'react';
import Modal from '@mui/joy/Modal';
import ModalClose from '@mui/joy/ModalClose';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import { Cities, IVenue, VenueTags, SubmissionType } from 'btdt-types';
import { FaPaperPlane } from 'react-icons/fa';
import { TagLabels } from '../consts';
import { useCreateSubmissionMutation } from '../hooks/useSubmission';
import { useRecaptchaToken } from '../hooks/useRecaptcha';
import DuplicateVenueWarning from './DuplicateVenueWarning';
import { useDevices } from '../helpers/devices';
import venuesDataProvider from '../data/venuesDataProvider';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYW1pcm96ZXIiLCJhIjoiY2xiODN3OHp5MDgzdzNvbXRzbHRmNDh6byJ9.z32MJnbhRaPU0qEXgjq7bQ';

interface IProps {
  open: boolean;
  coordinates: [number, number];
  address: string;
  city: Cities;
  onAddressChange(address: string, coords: [number, number]): void;
  onClose(): void;
  onSuccess(): void;
}

function AddVenueModal({ open, coordinates, address, city, onAddressChange, onClose, onSuccess }: IProps): React.ReactElement {
  const { isMobile } = useDevices();
  const { venues } = venuesDataProvider.useSearchVenuesQuery({ city });
  const [name, setName] = React.useState('');
  const [debouncedName, setDebouncedName] = React.useState('');
  const [nameEn, setNameEn] = React.useState('');
  const [yearsCertain, setYearsCertain] = React.useState('');
  const [yearOpened, setYearOpened] = React.useState('');
  const [yearClosed, setYearClosed] = React.useState('');
  const [tags, setTags] = React.useState<VenueTags[]>([]);
  const [source, setSource] = React.useState('');
  const [photos, setPhotos] = React.useState<File[]>([]);
  const [photoCredits, setPhotoCredits] = React.useState<string[]>([]);
  const [photoCaptions, setPhotoCaptions] = React.useState<string[]>([]);
  const [copyrightConfirmed, setCopyrightConfirmed] = React.useState(false);
  const [submitterName, setSubmitterName] = React.useState(sessionStorage.getItem('userName') || '');
  const [submitterEmail, setSubmitterEmail] = React.useState(sessionStorage.getItem('userEmail') || '');
  const [submitted, setSubmitted] = React.useState(false);
  const [validationError, setValidationError] = React.useState('');

  const getRecaptchaToken = useRecaptchaToken();

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(name), 300);
    return () => clearTimeout(timer);
  }, [name]);

  const { isLoading: isSubmitting, mutate } = useCreateSubmissionMutation({
    onSuccess: () => setSubmitted(true),
  });

  const handleAddressChange = async (value: string) => {
    if (value.length < 5) return;
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json?access_token=${MAPBOX_TOKEN}&language=he&country=IL`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        onAddressChange(value, [lat, lng]);
      }
    } catch (error) {
      console.error('Forward geocoding failed:', error);
    }
  };

  const handleTagToggle = (tag: VenueTags) => {
    setTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  };

  const handlePhotosChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setPhotos((prev) => [...prev, ...files]);
    setPhotoCredits((prev) => [...prev, ...files.map(() => '')]);
    setPhotoCaptions((prev) => [...prev, ...files.map(() => '')]);
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoCredits((prev) => prev.filter((_, i) => i !== index));
    setPhotoCaptions((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePhotoCredit = (index: number, credit: string) => {
    setPhotoCredits((prev) => prev.map((c, i) => i === index ? credit : c));
  };

  const updatePhotoCaption = (index: number, caption: string) => {
    setPhotoCaptions((prev) => prev.map((c, i) => i === index ? caption : c));
  };

  const handleSuggestEdit = (venue: IVenue) => {
    window.location.href = `/venues/${venue.id}`;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setValidationError('');

    if (!name.trim()) {
      setValidationError('יש להזין שם');
      return;
    }
    if (!yearsCertain.trim() && !yearOpened && !yearClosed) {
      setValidationError('יש להזין לפחות שנה אחת');
      return;
    }
    if (photos.length > 0 && !copyrightConfirmed) {
      alert('יש לאשר את תנאי השימוש בתמונות');
      return;
    }

    sessionStorage.setItem('userName', submitterName);
    sessionStorage.setItem('userEmail', submitterEmail);

    let recaptchaToken: string | undefined;
    try {
      recaptchaToken = await getRecaptchaToken();
    } catch (error) {
      console.error('reCAPTCHA token failed:', error);
    }

    mutate({
      type: SubmissionType.NEW_VENUE,
      venueData: JSON.stringify({
        name, nameEn, address, coordinates, city,
        yearsCertain,
        yearOpened: yearOpened ? parseInt(yearOpened) : undefined,
        yearClosed: yearClosed ? parseInt(yearClosed) : undefined,
        tags,
      }),
      source,
      submitterName,
      submitterEmail,
      photos,
      photoCredits,
      photoCaptions,
      recaptchaToken,
    });
  };

  const handleClose = () => {
    if (submitted) {
      onSuccess();
    } else {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-start' : 'center', marginTop: isMobile ? '24px' : 0 }}
    >
      <div style={{ position: 'relative' }}>
        <ModalClose
          variant="outlined"
          sx={{
            top: 'calc(-1/4 * var(--IconButton-size))',
            left: 'calc(-1/4 * var(--IconButton-size))',
            right: 'auto',
            boxShadow: '0 2px 12px 0 rgba(0 0 0 / 0.2)',
            borderRadius: '50%',
            bgcolor: 'background.surface',
          }}
        />
        {submitted ? (
          <div className="AddVenueModal">
            <div className="AddVenueModal-success">
              <p>תודה! המקום נשלח לבדיקה ויתווסף למפה אם נצליח לאמת את המידע.</p>
              <Button onClick={handleClose} size="sm" sx={{ marginTop: '12px' }}>סגירה</Button>
            </div>
          </div>
        ) : (
          <div className="AddVenueModal">
            <h2>הוספת מקום</h2>
            <form onSubmit={handleSubmit}>
              <div className="AddVenueModal-grid">
                <div className="full-width">
                  <label>כתובת</label>
                  <Input
                    value={address}
                    onChange={(e) => {
                      onAddressChange(e.target.value, coordinates);
                      handleAddressChange(e.target.value);
                    }}
                    size="sm"
                  />
                </div>

                <div>
                  <label>שם המקום *</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="sm"
                    placeholder="למשל: קפה כסית"
                  />
                </div>

                <div>
                  <label>שם באנגלית</label>
                  <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} size="sm" dir="ltr" />
                </div>

                <div className="full-width">
                  <DuplicateVenueWarning
                    name={debouncedName}
                    venues={venues}
                    onSuggestEdit={handleSuggestEdit}
                    onDismiss={() => {}}
                  />
                </div>

                <div className="full-width">
                  <label>שנים שבטוח שהמקום היה פעיל *</label>
                  <Input
                    value={yearsCertain}
                    onChange={(e) => setYearsCertain(e.target.value)}
                    size="sm"
                    placeholder="למשל: 1985-1992"
                  />
                </div>

                <div>
                  <label>שנת פתיחה</label>
                  <Input
                    type="number"
                    value={yearOpened}
                    onChange={(e) => setYearOpened(e.target.value)}
                    size="sm"
                  />
                </div>

                <div>
                  <label>שנת סגירה</label>
                  <Input
                    type="number"
                    value={yearClosed}
                    onChange={(e) => setYearClosed(e.target.value)}
                    size="sm"
                  />
                </div>

                <div className="full-width">
                  <label>קטגוריות</label>
                  <div className="AddVenueModal-tags">
                    {Object.values(VenueTags).map((tag) => (
                      <label key={tag}>
                        <input
                          type="checkbox"
                          checked={tags.includes(tag)}
                          onChange={() => handleTagToggle(tag)}
                        />
                        {TagLabels[tag]}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="full-width">
                  <label>מקור המידע</label>
                  <Input
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    size="sm"
                    placeholder="לינק לכתבה, אתר, פוסט או מקור אחר שיעזור לנו לאמת את המידע"
                  />
                </div>

                <div className="full-width">
                  <label>הוספת תמונות</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotosChange}
                    style={{ margin: '4px 0' }}
                  />
                  {photos.map((file, index) => (
                    <div key={index} className="AddVenueModal-photo-item">
                      <img src={URL.createObjectURL(file)} alt="" />
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Input
                          value={photoCaptions[index]}
                          onChange={(e) => updatePhotoCaption(index, e.target.value)}
                          size="sm"
                          placeholder="תיאור התמונה"
                        />
                        <Input
                          value={photoCredits[index]}
                          onChange={(e) => updatePhotoCredit(index, e.target.value)}
                          size="sm"
                          placeholder="קרדיט לצילום"
                        />
                      </div>
                      <button type="button" onClick={() => removePhoto(index)}>✕</button>
                    </div>
                  ))}

                  {photos.length > 0 && (
                    <div className="AddVenueModal-copyright">
                      <label>
                        <input
                          type="checkbox"
                          checked={copyrightConfirmed}
                          onChange={(e) => setCopyrightConfirmed(e.target.checked)}
                        />
                        התמונה מאושרת לשימוש ללא מטרות רווח
                      </label>
                    </div>
                  )}
                </div>

                <div>
                  <label>שם</label>
                  <Input
                    value={submitterName}
                    onChange={(e) => setSubmitterName(e.target.value)}
                    size="sm"
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label>דואר אלקטרוני</label>
                  <div dir={submitterEmail ? 'ltr' : 'rtl'}>
                    <Input
                      type="email"
                      value={submitterEmail}
                      onChange={(e) => setSubmitterEmail(e.target.value)}
                      size="sm"
                      autoComplete="email"
                    />
                  </div>
                </div>
              </div>

              {validationError && (
                <p style={{ color: '#ee5555', margin: '8px 0', fontSize: 14 }}>{validationError}</p>
              )}

              <Button
                type="submit"
                startDecorator={<FaPaperPlane />}
                disabled={isSubmitting}
                sx={{ marginTop: '12px' }}
              >
                שליחה
              </Button>
              <p style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
                אתר זה מוגן באמצעות reCAPTCHA של Google.
                {' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: '#aaa' }}>מדיניות פרטיות</a>
                {' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" style={{ color: '#aaa' }}>תנאי שימוש</a>
              </p>
            </form>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default AddVenueModal;
